/**
 * Path to Performance Helper Utilities
 * Reusable functions for P2P feature to reduce code duplication
 */

import { 
  CATEGORY_TYPE_MAP, 
  DEFAULT_ACTION_OPTION,
  RATING_COLORS,
  RATING_LABELS,
  RATING_UNRATED,
} from "../../constants/pathToPerformance";
import type { CategoryLevelsResponse } from "../../types/pathToPerformance";

/**
 * Get category type code from category name
 * @param categoryName - User-friendly category name (e.g., "Sponsorship")
 * @returns Category type code (e.g., "S") or null if not found
 */
export const getCategoryTypeCode = (categoryName: string): string | null => {
  return CATEGORY_TYPE_MAP[categoryName] || null;
};

/**
 * Get action needed label from actionId by searching category items
 * @param categoryType - Category type code (e.g., "S", "L", "T")
 * @param actionId - Action needed ID
 * @param categoryLevels - Category levels data from API
 * @returns Formatted action label or null
 */
export const getActionNeededLabel = (
  categoryType: string,
  actionId: number | null,
  categoryLevels: CategoryLevelsResponse | null,
): string | null => {
  if (!categoryLevels || !actionId) return null;

  const categoryItems = categoryLevels[categoryType as keyof CategoryLevelsResponse];
  if (!categoryItems) return null;

  const action = categoryItems.find((item) => item.actionNeededId === actionId);
  if (!action) return null;

  return `${action.actionNeededText} (Level ${action.actionNeededLvl})`;
};

/**
 * Find action ID from action needed label and category
 * @param category - Category name
 * @param actionNeeded - Action needed label
 * @param categoryLevels - Category levels data from API
 * @returns Action ID or null
 */
export const findActionId = (
  category: string,
  actionNeeded: string,
  categoryLevels: CategoryLevelsResponse | null,
): number | null => {
  if (!categoryLevels || actionNeeded === DEFAULT_ACTION_OPTION) {
    return null;
  }

  const catType = getCategoryTypeCode(category);
  if (!catType) return null;

  const categoryData = categoryLevels[catType as keyof CategoryLevelsResponse];
  if (!categoryData) return null;

  const found = categoryData.find(
    (opt) =>
      opt.actionNeededText === actionNeeded ||
      `${opt.actionNeededText} (Level ${opt.actionNeededLvl})` === actionNeeded,
  );

  return found ? found.actionNeededId : null;
};

/**
 * Generate kebab-case value string for action item
 * @param text - Action item text
 * @param level - Action item level
 * @returns Kebab-case value (e.g., "sponsors-identified-l1")
 */
export const generateActionValue = (text: string, level: number): string => {
  // Using replace with global regex for ES2020 compatibility
  /* eslint-disable unicorn/prefer-string-replace-all */
  const kebab = text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "") // Remove special chars except hyphens
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single
    .trim();
  /* eslint-enable unicorn/prefer-string-replace-all */

  return `${kebab}-l${level}`;
};

/**
 * Calculate rating from category level value
 * Maps category level (catgLvl) to rating displayed in tags
 * 
 * @param categoryLevel - Category level (0, 1-5, or 99)
 * @returns Rating number (0-5 or 99)
 */
export const calculateRatingFromLevel = (categoryLevel: number | null): number => {
  if (categoryLevel === null || categoryLevel === 0) return 0;
  
  // Level 99 = Not Applicable
  if (categoryLevel === 99) return 99;
  
  // Level 1-5 maps directly to rating 1-5
  return categoryLevel;
};

/**
 * Validate if an action item has a valid selection
 * @param actionNeeded - Action needed label
 * @param categoryLevel - Category level value
 * @returns True if action item has valid selection
 */
export const isActionItemSelected = (
  actionNeeded: string,
  categoryLevel: number,
): boolean => {
  return (
    actionNeeded !== DEFAULT_ACTION_OPTION &&
    categoryLevel !== RATING_UNRATED
  );
};

/**
 * Normalize rating value to number
 * Handles both string and number inputs safely
 * @param rating - Rating value (string or number)
 * @returns Normalized rating number, or 0 if invalid
 */
export const normalizeRating = (rating: number | string): number => {
  if (typeof rating === "number") {
    return Number.isNaN(rating) ? RATING_UNRATED : rating;
  }
  
  const parsed = Number.parseInt(rating, 10);
  return Number.isNaN(parsed) ? RATING_UNRATED : parsed;
};

/**
 * Get background color class for a rating value
 * Returns appropriate Tailwind CSS class based on rating level
 * @param rating - Rating value (1-5, 99, or 0)
 * @returns Tailwind CSS background color class
 */
export const getRatingColor = (rating: number | string): string => {
  const ratingNum = normalizeRating(rating);
  return RATING_COLORS[ratingNum as keyof typeof RATING_COLORS] || RATING_COLORS[0];
};

/**
 * Get accessible label for a rating value
 * @param rating - Rating value
 * @returns Human-readable rating description
 */
export const getRatingLabel = (rating: number | string): string => {
  const ratingNum = normalizeRating(rating);
  return RATING_LABELS[ratingNum as keyof typeof RATING_LABELS] || RATING_LABELS[0];
};

/**
 * Validate if rating is within valid range
 * @param rating - Rating value to validate
 * @returns True if rating is valid (1-5, 99, or 0)
 */
export const isValidRating = (rating: number | string): boolean => {
  const ratingNum = normalizeRating(rating);
  return ratingNum in RATING_COLORS;
};
